/*
 * Copyright (c) 2025 AirAsia Berhad (Malaysia). All rights reserved.
 *
 * This software is proprietary and confidential. Unauthorized copying of this file,
 * via any medium, is strictly prohibited. Do not distribute, copy, modify, or reuse
 * any part of this code without explicit written permission from AirAsia Move.
 *
 * For internal use only.
 */

import { format } from 'date-fns';
import { getMonthsBetweenDates } from '@airasia-phoenix/react-native/utils/dateUtils';
import { CalendarPriceObject } from '@airasia-phoenix/react-native/widgets/DatePickerView/types';
import Constants from './constants';
import { LowFareItem } from './types';
import { TripType } from '../../types/FlightSearch.types';

type CalendarPriceEntry = {
  displayPriceString?: string;
  price?: number;
  soldOut?: boolean;
  soldOutLabel?: string;
  noFlights?: boolean;
  priceStringColor?: string;
};

export const get_empty_fares = (
  startDate: Date,
  endDate: Date,
): CalendarPriceObject => {
  // for each month an object - [{},{}...] number of months equal object
  return Array.from(
    {
      length: getMonthsBetweenDates(startDate, endDate) + 1, // 1 added because this excludes start month
    },
    (_1, _2) => {
      return {};
    },
  );
};

export const normalizeApiDateToCalendarKey = (dateStr: string): string => {
  if (/^\d{2}\/\d{2}\/\d{4}$/.test(dateStr)) return dateStr;

  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(dateStr);
  if (m) {
    const [, yyyy, mm, dd] = m;
    return `${dd}/${mm}/${yyyy}`;
  }

  return dateStr;
};

export const extractFareItems = (resp: unknown): LowFareItem[] => {
  const r = resp as { data?: unknown };
  if (Array.isArray(r?.data)) return r.data as LowFareItem[];
  return [];
};

export const getCalendarComparisonDate = (args: {
  activeField: 'depart' | 'return';
  selectedDates: (Date | undefined)[];
  tripType: TripType;
}) => {
  const { activeField, selectedDates, tripType } = args;
  const [departDate, returnDate] = selectedDates;

  if (tripType === TripType.ONE_WAY) {
    return departDate ?? null;
  }

  if (activeField === 'return') {
    return returnDate ?? departDate ?? null;
  }

  return departDate ?? returnDate ?? null;
};

export const findSelectedFarePrice = (args: {
  fareGroups: LowFareItem[][];
  selectedDate: Date | null;
  cachedFares?: CalendarPriceObject;
}) => {
  const { fareGroups, selectedDate, cachedFares = [] } = args;

  if (!selectedDate) {
    return undefined;
  }

  const selectedDateKey = format(selectedDate, Constants.LOW_FARE_DATE_FORMAT);

  const fareFromVisibleMonths = fareGroups
    .flat()
    .find((fare) => fare.departureDate === selectedDateKey);

  if (typeof fareFromVisibleMonths?.price === 'number') {
    return fareFromVisibleMonths.price;
  }

  for (const monthPriceObject of cachedFares) {
    const cachedPrice = monthPriceObject?.[selectedDateKey]?.price;

    if (typeof cachedPrice === 'number') {
      return cachedPrice;
    }
  }

  return undefined;
};

export const hasMonthPriceObjectChanged = (
  previous: Record<string, CalendarPriceEntry> | undefined,
  next: Record<string, CalendarPriceEntry>,
) => {
  const previousKeys = previous ? Object.keys(previous) : [];
  const nextKeys = Object.keys(next);

  if (previousKeys.length !== nextKeys.length) {
    return true;
  }

  return nextKeys.some((key) => {
    const previousEntry = previous?.[key];
    const nextEntry = next[key];

    return (
      previousEntry?.displayPriceString !== nextEntry?.displayPriceString ||
      previousEntry?.price !== nextEntry?.price ||
      previousEntry?.soldOut !== nextEntry?.soldOut ||
      previousEntry?.soldOutLabel !== nextEntry?.soldOutLabel ||
      previousEntry?.noFlights !== nextEntry?.noFlights ||
      previousEntry?.priceStringColor !== nextEntry?.priceStringColor
    );
  });
};

export const deriveVisibleMonthsFromRanges = (ranges: Date[][]): string[] => {
  const monthKeySet = new Set<string>();
  for (const range of ranges) {
    const start = range?.[0];
    if (start) {
      monthKeySet.add(toMonthKey(start));
    }
  }
  return Array.from(monthKeySet);
};

export const shouldCommitVisibleMonths = (
  visibleMonthKeys: string[],
  pendingTargetMonthKey: string | null,
): boolean => {
  return (
    !pendingTargetMonthKey || visibleMonthKeys.includes(pendingTargetMonthKey)
  );
};

/**
 * Converts a Date into a stable YYYY-MM key for
 * month-based grouping & caching
 */
export const toMonthKey = (date: Date): string => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  return `${y}-${m}`;
};

/**
 * Walks month-by-month from `start` to `end` inclusive, returning
 * every calendar month key ('yyyy-MM') the range spans.
 */
export const getMonthKeysBetween = (start: Date, end: Date): string[] => {
  const keys: string[] = [];
  const cursor = new Date(start.getFullYear(), start.getMonth(), 1);
  const last = new Date(end.getFullYear(), end.getMonth(), 1);

  while (cursor.getTime() <= last.getTime()) {
    keys.push(toMonthKey(cursor));
    cursor.setMonth(cursor.getMonth() + 1);
  }

  return keys;
};

const addDays = (date: Date, days: number): Date | undefined => {
  if (!date) return undefined;
  const result = new Date(date);
  result.setDate(result.getDate() + days);
  return result;
};

export const validateDatesBeforeConfirm = (
  departDate?: Date,
  returnDate?: Date,
): [Date | undefined, Date | undefined] => {
  if (!departDate && !returnDate) return [undefined, undefined];

  const daysGap = Math.max(
    0,
    Constants.DEFAULT_RETURN_DAYS - Constants.DEFAULT_DEPART_DAYS,
  );

  // only return Selected treat it as Depar
  if (!departDate && returnDate) {
    const newDepart = returnDate;
    return [newDepart, addDays(newDepart, daysGap)];
  }

  // depart exists but return empty → auto generate return
  if (departDate && !returnDate) {
    return [departDate, addDays(departDate, daysGap)];
  }

  // depart > return → clear return
  if (departDate && returnDate && departDate > returnDate) {
    return [departDate, undefined];
  }

  return [departDate, returnDate];
};
